import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LANDING_KEYS, getLandingParams } from '../lib/landingAttribution';

export interface AnswerSnapshot {
  /** Selected option ids (single-select has one). */
  optionIds: string[];
  /** Free-text payload for _freeText / _custom. */
  freeText?: string;
  /** ms timestamp of the most recent answer. */
  ts: number;
}

export interface QuizState {
  sessionId: string | null;
  currentQuestionId: string | null;
  answers: Record<string, AnswerSnapshot>;
  /** Last-write-wins string-coerced variables (per the CSV's Variable / Variable value columns). */
  variables: Record<string, string>;
  /** UTM + click ids captured at first session creation. */
  attribution: Record<string, string>;
  /** Quiz variant captured from `?quiz=...` on first session creation —
   *  e.g. "chris" or "breanden". Used by the admin funnel to split traffic
   *  between named quiz variants. Lowercased on capture. */
  quiz: string | null;
  /** Set after lead capture. */
  leadId: string | null;
  /** Email captured at lead step. Used to pre-fill /login + show "sign in" UX in Intro/ThankYou. */
  leadEmail: string | null;
  /** Name captured at lead step — drives the personalized hero on /pricing. */
  leadName: string | null;
  /** Phone captured at lead step (optional). Forwarded to the GTM `user` block on quizComplete + purchase. */
  leadPhone: string | null;
  /** ms timestamp the user clicked Get Started. Drives the time-investment validator on /pricing. */
  quizStartedAt: number | null;
  /** ms timestamp the user reached the terminal question. Both timestamps make the time delta available. */
  quizCompletedAt: number | null;
  /** Set after a plan card is clicked on /pricing. */
  selectedPlan: 'monthly' | 'quarterly' | 'annual' | null;
  /**
   * Legacy whole-report trigger flag. Kept for backward compatibility but
   * superseded by the per-section flags below.
   */
  reportTriggered: boolean;
  /**
   * Per-section generation flags. We fire each section's generator the
   * moment the user has answered the questions that feed it — so by the
   * time the quiz ends, all 3 sections are usually already in the DB.
   * Persisted so reloads/back-button don't refire and waste tokens.
   */
  sectionsTriggered: Partial<Record<'identity' | 'careers' | 'strategy', boolean>>;
  /**
   * True while a paying customer is retaking the quiz from Account & billing.
   * Completion skips lead capture/pricing and goes to /retake/building, and
   * per-section pre-generation is off (the full report is built once, at the
   * end, by report-retake-background).
   */
  retake: boolean;
}

interface QuizActions {
  ensureSession: () => void;
  setAnswer: (questionId: string, snapshot: AnswerSnapshot) => void;
  setVariable: (name: string, value: string) => void;
  setCurrentQuestionId: (id: string | null) => void;
  setLead: (leadId: string, info?: { email?: string; name?: string | null; phone?: string | null }) => void;
  setPlan: (plan: 'monthly' | 'quarterly' | 'annual') => void;
  markQuizStarted: () => void;
  markQuizCompleted: () => void;
  /** Swap in a fresh server-created retake session (empty answers). */
  beginRetake: (info: {
    sessionId: string; leadId: string; email: string;
    name: string | null; phone: string | null; retakeOf: string;
  }) => void;
  finishRetake: () => void;
  reset: () => void;
}

type Store = QuizState & QuizActions;

const initialState: QuizState = {
  sessionId: null,
  currentQuestionId: null,
  answers: {},
  variables: {},
  attribution: {},
  quiz: null,
  leadId: null,
  leadEmail: null,
  leadName: null,
  leadPhone: null,
  quizStartedAt: null,
  quizCompletedAt: null,
  selectedPlan: null,
  reportTriggered: false,
  sectionsTriggered: {},
  retake: false,
};

type SectionName = 'identity' | 'careers' | 'strategy';

// CAPBOI Likert variables — when at least 4 of these are set, we have enough
// signal to drive a meaningful personality section. List taken from
// netlify/lib/capboiScoring.ts contributor tables.
const CAPBOI_LIKERTS = [
  'analyticalSkillsScore', 'dataAnalysisScore', 'problemSolvingScore',
  'attentionToDetailScore', 'strategicThinkingScore', 'technicalSkillsScore',
  'communicationSkillsScore', 'interpersonalSkillsScore', 'teamManagementScore',
  'leadershipConfidenceScore', 'negotiationSkillsScore',
  'organizationalSkillsScore', 'projectManagementScore', 'timeManagementScore',
  'creativeThinkerScore', 'innovationSkillsScore',
  'decisionMakingScore',
] as const;

const MIN_CAPBOI_FOR_IDENTITY = 4;

/**
 * Per-section trigger criteria. Each function returns true when the section
 * has enough quiz signal to be generated meaningfully. Criteria-based (not
 * tied to specific question IDs) so quiz path variations / skipped branches
 * still fire each section eventually.
 */
const SECTION_READY: Record<SectionName, (variables: Record<string, string>, completed: boolean) => boolean> = {
  identity: (v, completed) =>
    completed
    || CAPBOI_LIKERTS.filter((k) => v[k] && v[k].length > 0).length >= MIN_CAPBOI_FOR_IDENTITY,
  // Careers normally fires on salary OR job, but for paths that skip both
  // (e.g. students with no work history) we fall back to quiz completion.
  careers: (v, completed) =>
    completed || !!(v.currentSalary || v.currentJob || v.currentJobOther),
  // Strategy needs full context — wait for salary OR quiz completion.
  strategy: (v, completed) => completed || !!v.currentSalary,
};

async function fireSection(
  sessionId: string,
  section: SectionName,
  variables: Record<string, string>,
  leadEmail: string | null,
  leadName: string | null,
): Promise<void> {
  if (typeof fetch === 'undefined') return;
  try {
    // Send the latest variables ALONG WITH the request so the endpoint
    // doesn't race against save-progress's 300ms debounce. Without this
    // the section endpoint reads stale DB data on fast-quiz users —
    // their fresh CAPBOI Likerts haven't landed in DB yet, so scores
    // collapse to neutral → "Strategist" no-signal default.
    await fetch('/api/reports-generate-section-background', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_session_id: sessionId,
        section,
        // Optional overrides — server falls back to DB if these are absent
        variables,
        lead_email: leadEmail,
        lead_name: leadName,
      }),
    });
  } catch {
    // Caller clears the flag so a later trigger can retry.
  }
}

/**
 * Look at the current store state — fire every section whose ready-criteria
 * is met AND that hasn't been triggered yet. Designed to be called from:
 *   • setVariable (real-time during quiz)
 *   • setLead (catch any sections missed by quiz-path skips)
 *   • markQuizCompleted (last-chance fire for any missing section)
 *   • initial hydration (resume from localStorage)
 */
function fireSectionsAsAvailable(
  state: QuizState,
  setSectionsTriggered: (next: QuizState['sectionsTriggered']) => void,
): void {
  if (!state.sessionId) return;
  // Retakes build the full report once, at the end.
  if (state.retake) return;
  const triggered = { ...(state.sectionsTriggered ?? {}) };
  const completed = state.quizCompletedAt != null;
  let changed = false;

  for (const section of ['identity', 'careers', 'strategy'] as const) {
    if (triggered[section]) continue;
    if (!SECTION_READY[section](state.variables, completed)) continue;
    triggered[section] = true;
    changed = true;
    void fireSection(
      state.sessionId,
      section,
      state.variables,
      state.leadEmail,
      state.leadName,
    ).catch(() => {
      // Failed — note: we already optimistically marked it triggered. A
      // follow-up call (next variable, lead capture, completion) will see
      // the flag as true and skip. For now we live with that — the lazy
      // stub gen in /api/reports-by-session covers the floor case.
    });
  }

  if (changed) setSectionsTriggered(triggered);
}

const ATTR_KEYS = LANDING_KEYS;

function captureAttribution(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  // Boot-time stash first (survives redirects and untagged later visits),
  // then anything on the current URL on top.
  const result: Record<string, string> = { ...getLandingParams() };
  for (const k of ATTR_KEYS) {
    const v = params.get(k);
    if (v) result[k] = v;
  }
  if (document.referrer) result.referrer = document.referrer;
  return result;
}

/** Read `?quiz=...` from the current URL and normalize to a lowercase string. */
function captureQuiz(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('quiz');
  if (!raw) return null;
  const trimmed = raw.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

function makeSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Cheap fallback (browsers without crypto.randomUUID — extremely rare).
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export const useQuizStore = create<Store>()(
  persist(
    (set, get) => ({
      ...initialState,
      ensureSession: () => {
        if (!get().sessionId) {
          set({
            sessionId: makeSessionId(),
            attribution: captureAttribution(),
            quiz: captureQuiz(),
          });
        } else {
          // Existing session: add any landing tags it doesn't have yet
          // (e.g. a returning visitor who now arrived via the review site).
          // Never overwrites keys already recorded.
          const cur = get().attribution ?? {};
          const add: Record<string, string> = {};
          for (const [k, v] of Object.entries(getLandingParams())) {
            if (v && !(k in cur)) add[k] = v;
          }
          if (Object.keys(add).length > 0) set({ attribution: { ...cur, ...add } });
        }
        // Resume case: persisted variables may already meet the section
        // readiness criteria from a previous page load. Fire any sections
        // whose criteria pass but flag is still false.
        fireSectionsAsAvailable(get(), (next) => set({ sectionsTriggered: next }));
      },
      setAnswer: (questionId, snapshot) =>
        set((state) => ({ answers: { ...state.answers, [questionId]: snapshot } })),
      setVariable: (name, value) => {
        set((state) => ({ variables: { ...state.variables, [name]: value } }));
        fireSectionsAsAvailable(get(), (next) => set({ sectionsTriggered: next }));
        // `name` is implicitly checked via the readiness predicates above — no
        // need to switch on the specific variable name. This also means the
        // trigger handles the resume case (variables set on a previous page
        // load count toward the readiness check on the first subsequent set).
        void name;
      },
      setCurrentQuestionId: (id) => set({ currentQuestionId: id }),
      setLead: (leadId, info) => {
        set((state) => ({
          leadId,
          leadEmail: info?.email ?? state.leadEmail,
          leadName: info?.name ?? state.leadName,
          leadPhone: info?.phone ?? state.leadPhone,
        }));
        // Lead capture is a last-chance fire — by here, the user has
        // committed and we want any remaining sections to start ASAP.
        fireSectionsAsAvailable(get(), (next) => set({ sectionsTriggered: next }));
      },
      setPlan: (plan) => set({ selectedPlan: plan }),
      markQuizStarted: () =>
        set((state) => (state.quizStartedAt ? state : { quizStartedAt: Date.now() })),
      markQuizCompleted: () => {
        set((state) => (state.quizCompletedAt ? state : { quizCompletedAt: Date.now() }));
        // Final fire — every section's strategy readiness check passes
        // once `quizCompletedAt` is set, so any straggler section fires here.
        fireSectionsAsAvailable(get(), (next) => set({ sectionsTriggered: next }));
      },
      beginRetake: (info) =>
        set({
          ...initialState,
          sessionId: info.sessionId,
          leadId: info.leadId,
          leadEmail: info.email,
          leadName: info.name,
          leadPhone: info.phone,
          quiz: 'retake',
          attribution: { retake_of: info.retakeOf },
          quizStartedAt: Date.now(),
          retake: true,
        }),
      finishRetake: () => set({ retake: false }),
      reset: () => set({ ...initialState }),
    }),
    {
      name: 'jobtest-quiz-v1',
      version: 1,
      partialize: (s) => ({
        sessionId: s.sessionId,
        currentQuestionId: s.currentQuestionId,
        answers: s.answers,
        variables: s.variables,
        attribution: s.attribution,
        quiz: s.quiz,
        leadId: s.leadId,
        leadEmail: s.leadEmail,
        leadName: s.leadName,
        leadPhone: s.leadPhone,
        quizStartedAt: s.quizStartedAt,
        quizCompletedAt: s.quizCompletedAt,
        selectedPlan: s.selectedPlan,
        reportTriggered: s.reportTriggered,
        sectionsTriggered: s.sectionsTriggered,
        retake: s.retake,
      }),
    },
  ),
);

// ---------- Server sync (debounced + pagehide beacon) ----------

const SYNC_DEBOUNCE_MS = 300;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let lastBody = '';

function buildBody(state: QuizState): string {
  return JSON.stringify({
    client_session_id: state.sessionId,
    current_question_id: state.currentQuestionId,
    answers: state.answers,
    variables: state.variables,
    attribution: state.attribution,
    quiz: state.quiz,
    lead_id: state.leadId,
    selected_plan: state.selectedPlan,
  });
}

if (typeof window !== 'undefined') {
  useQuizStore.subscribe((state) => {
    if (!state.sessionId) return;
    const body = buildBody(state);
    if (body === lastBody) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      lastBody = body;
      void fetch('/api/save-progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => {
        // Backend down or offline — drop. Next state change re-sends the
        // full snapshot; we don't queue since each save is idempotent
        // upsert by client_session_id.
      });
    }, SYNC_DEBOUNCE_MS);
  });

  // Pagehide beacon — survives navigation away. Beacon API is best-effort;
  // body capped at ~64KB so small enough since we send a snapshot.
  window.addEventListener('pagehide', () => {
    const state = useQuizStore.getState();
    if (!state.sessionId) return;
    const body = buildBody(state);
    if (body === lastBody) return;
    try {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon('/api/save-progress', blob);
    } catch {
      void fetch('/api/save-progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  });
}
